/**
 * Lo que sale del panel cuando alguien decide (SPEC-014 CA-11).
 *
 * Vive fuera del componente porque es lo único de esa pantalla que puede
 * equivocarse sin que se note: un botón que dice «No es» y manda `CONFIRMAR`
 * se ve perfecto y resuelve el caso al revés. Aquí se puede probar sin DOM.
 */

import type { Decision } from "../ganchos/datos.ts";

/**
 * @param clave la del movimiento que se está mirando, y de ningún otro
 * @param accion lo que dice el botón
 * @param cliente a quién se le atribuye. Solo tiene sentido al ETIQUETAR
 * @param facturas contra qué se aplica. Aparte del cliente a propósito: el
 *   nombre se repite y por eso se aprende de él; el número de factura es único
 *   por pago, y meterlo dentro del nombre deja el aprendizaje en cero
 * @param nota lo que escribió la persona, si escribió algo
 */
export function cargaDecision({
  clave,
  accion,
  cliente = "",
  clienteNit = "",
  facturas = [],
  abonos = [],
  nota = "",
}: {
  clave: string;
  accion: Decision["accion"];
  cliente?: string;
  clienteNit?: string;
  facturas?: string[];
  abonos?: string[];
  nota?: string;
}): Decision {
  if (accion === "ETIQUETAR" && !cliente.trim()) {
    // Un ETIQUETAR sin cliente le pide al servidor que atribuya el pago a
    // nadie. La pantalla ya no deja mandarlo, pero eso es una condición de un
    // `onSubmit`: si mañana alguien añade un atajo de teclado, esto sigue aquí.
    throw new Error("Etiquetar exige decir a quién.");
  }

  return {
    clave,
    accion,
    // Los campos vacíos NO viajan: `{cliente: ""}` no es lo mismo que no
    // mandar cliente, y el servidor tendría que adivinar cuál de los dos quiso
    // decir quien pulsó.
    ...(accion === "ETIQUETAR" && cliente.trim() ? { cliente: cliente.trim() } : {}),
    ...(limpiar(facturas).length ? { facturas: limpiar(facturas) } : {}),
    // La identidad solo viaja al etiquetar: es a quién se le atribuye el pago.
    ...(accion === "ETIQUETAR" && clienteNit.trim() ? { clienteNit: clienteNit.trim() } : {}),
    ...(accion === "LIGAR" && limpiar(abonos).length ? { abonos: limpiar(abonos) } : {}),
    ...(nota.trim() ? { nota: nota.trim() } : {}),
  };
}

/**
 * Sin espacios sobrantes, sin vacías y sin repetidas.
 *
 * Lo de las repetidas no es cosmético: escribir «FVEL 949» dos veces al
 * corregir haría creer que un pago cubre dos cargos cuando cubre uno.
 */
const limpiar = (facturas: string[]) => [
  ...new Set(facturas.map((f) => f.trim()).filter(Boolean)),
];

/**
 * Lo que se le dice a la persona después de decidir (SPEC-029 §7).
 *
 * Cada acción dice qué pasó con el pago, no qué hizo el sistema: «No es de ese
 * cliente: sigue en la bandeja» deja claro que el pago no se perdió, que era
 * justo lo que pasaba antes con DESCARTAR.
 */
export function mensajeDeDecision(d: Decision, quienPago: string): string {
  const de = quienPago ? ` de ${quienPago}` : "";
  switch (d.accion) {
    case "ETIQUETAR":
      return `Anotado: ese pago es de ${d.cliente}. El próximo del mismo pagador se resolverá solo.`;
    case "RECHAZAR":
      return `Anotado: el pago${de} no es de ese cliente. Sigue en la bandeja para decir de quién es.`;
    case "DESCARTAR":
      return `Listo: el pago${de} no es nuestro. Ya no aparece en la bandeja.`;
    case "ANULAR":
      return "Deshecho. El pago volvió a como estaba.";
    case "LIGAR":
      return `Listo: el pago${de} quedó ligado al pago registrado. Ya no aparece en las alertas.`;
    default:
      return `Confirmado el pago${de}${d.facturas?.length ? ` con ${d.facturas.join(", ")}` : ""}.`;
  }
}
